import { type FC, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { F0Box, F0Text } from '@factorialco/f0-react'
import { OneEmptyState, Page, Spinner } from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { DetailSidebar } from 'modules/it_management/components/device_catalog/DetailSidebar'
import { DeviceImageCarousel } from 'modules/it_management/components/device_catalog/DeviceImageCarousel'
import { DeviceSpecifications } from 'modules/it_management/components/device_catalog/DeviceSpecifications'
import { MarketplaceFAQ } from 'modules/it_management/components/device_catalog/MarketplaceFAQ'
import {
  INSURANCE_PRICE,
  IT_SUBSCRIPTION_PRICE,
} from 'modules/it_management/hooks/device_catalog/constants'
import type { CartItem } from 'modules/it_management/hooks/device_catalog/types'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { useDeviceCatalogSavedDevices } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogSavedDevices'
import { navigation } from 'modules/it_management/lib/deviceCatalogNavigation'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'
import { PRODUCT_CATALOG_VARIANTS_DOCUMENT } from 'modules/it_management/lib/productCatalogGraphql'

import emptyStateImage from './Empty-state-device-catalog-card.png'

function buildFaqItems() {
  return [
    {
      id: 'q1',
      question: i18n.t('it_management.device_catalog.faq.q1'),
      answer: i18n.t('it_management.device_catalog.faq.a1'),
    },
    {
      id: 'q2',
      question: i18n.t('it_management.device_catalog.faq.q2'),
      answer: i18n.t('it_management.device_catalog.faq.a2'),
    },
    {
      id: 'q3',
      question: i18n.t('it_management.device_catalog.faq.q3'),
      answer: i18n.t('it_management.device_catalog.faq.a3'),
    },
    {
      id: 'q4',
      question: i18n.t('it_management.device_catalog.faq.q4'),
      answer: i18n.t('it_management.device_catalog.faq.a4'),
    },
    {
      id: 'q5',
      question: i18n.t('it_management.device_catalog.faq.q5'),
      answer: i18n.t('it_management.device_catalog.faq.a5'),
    },
    {
      id: 'q6',
      question: i18n.t('it_management.device_catalog.faq.q6'),
      answer: i18n.t('it_management.device_catalog.faq.a6'),
    },
  ]
}

const DeviceCatalogDetailPage: FC = () => {
  const { pathname } = useLocation()
  const params = useParams<{ variantId: string }>()
  const variantIdFromParams = params.variantId
  const { queryContext, buildProductImageUrl, loadingProductCatalogContext } = useProductCatalog()
  const { company, access } = useCurrentSession()
  const showDeviceAddOns = false
  const showSantanderLegalBlock = false

  // Derive variantId from URL when params aren't ready yet (e.g. after layout cart fix).
  const pathSegments = pathname.split('/').filter(Boolean)
  const deviceCatalogIndex = pathSegments.indexOf(navigation.path)
  const isDetailPath = deviceCatalogIndex >= 0 && deviceCatalogIndex < pathSegments.length - 1
  const variantIdFromPath = isDetailPath
    ? (pathSegments[deviceCatalogIndex + 1] ?? undefined)
    : undefined
  const variantId = variantIdFromParams ?? variantIdFromPath

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const { data, loading, error, networkActivity } = useQuery(PRODUCT_CATALOG_VARIANTS_DOCUMENT, {
    variables: { ids: variantId ? [variantId] : [] },
    skip: !variantId || loadingProductCatalogContext,
    context: queryContext,
  })

  const variant = data?.itManagement.distributorProductVariantsConnection?.nodes?.[0] || null

  const [selectedCountry, setSelectedCountry] = useState('spain')
  const [keyboardLanguage, setKeyboardLanguage] = useState('en-us')
  const [insuranceEnabled, setInsuranceEnabled] = useState(false)
  const [itSubscriptionEnabled, setItSubscriptionEnabled] = useState(false)
  const [selectedOfferId, setSelectedOfferId] = useState<string>('')
  const [quantity, setQuantity] = useState(1)
  const effectiveInsuranceEnabled = showDeviceAddOns && insuranceEnabled
  const effectiveItSubscriptionEnabled = showDeviceAddOns && itSubscriptionEnabled

  const { toggleSaved, isSaved, isPending } = useDeviceCatalogSavedDevices()

  const cart = useDeviceCatalogCartContext()
  const { updateHeader } = useResourceHeader()

  useEffect(() => {
    if (variant) {
      track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
        device_id: variant.id,
        device_name: variant.product?.name || variant.sku || variant.id,
        coming_from: document.referrer || 'direct',
        company_id: company.id,
        user_id: access.id,
      })
    }
  }, [variant, company.id, access.id])

  // Update resource header from federated query result (route meta no longer runs the query).
  // Use a device-specific title only: never pass the generic "Device details" so header/breadcrumb
  // don’t stay generic for variants with missing product name/sku (avoids flaky "Device details").
  useEffect(() => {
    if (!variant) return
    const genericTitle = i18n.t('it_management.device_catalog.detail.title')
    const rawName = variant.product?.name || variant.sku || variant.id || genericTitle
    const deviceName = rawName === genericTitle && variant.id ? variant.id : rawName
    const category = variant.product?.typeName ?? ''
    const brand = variant.product?.brand ?? ''
    const sku = variant.sku ?? ''
    const hasZeroTouch =
      variant.productAttributes?.some(
        (attribute) =>
          attribute.name.toLowerCase().trim() === 'zero-touch' &&
          attribute.value.toLowerCase().trim() === 'yes'
      ) ?? false
    const deliveryInfo =
      variant.deliveryConditions &&
      variant.deliveryConditions.trim() !== '' &&
      variant.deliveryConditions !== '-'
        ? variant.deliveryConditions
        : undefined
    const metadata: Array<{ label: string; value: { type: 'text'; content: string } }> = []
    if (category) {
      metadata.push({
        label: i18n.t('it_management.device_catalog.detail.metadata_category'),
        value: { type: 'text', content: category },
      })
    }
    if (brand) {
      metadata.push({
        label: i18n.t('it_management.device_catalog.detail.metadata_brand'),
        value: { type: 'text', content: brand },
      })
    }
    if (deliveryInfo) {
      metadata.push({
        label: i18n.t('it_management.device_catalog.detail.metadata_delivery'),
        value: { type: 'text', content: deliveryInfo },
      })
    }
    if (hasZeroTouch) {
      metadata.push({
        label: i18n.t('it_management.device_catalog.detail.metadata_zero_touch'),
        value: { type: 'text', content: '' },
      })
    }
    if (sku) {
      metadata.push({
        label: i18n.t('it_management.device_catalog.detail.metadata_sku'),
        value: { type: 'text', content: sku },
      })
    }
    updateHeader({ title: deviceName, metadata })
  }, [variant, updateHeader])

  useEffect(() => {
    if (variant?.offers && variant.offers.length > 0) {
      const defaultId =
        variant.offers.find((o) => o.id === variant.firstOfferId)?.id ?? variant.offers[0]?.id ?? ''
      setSelectedOfferId(defaultId)
    }
  }, [variant?.id, variant?.firstOfferId, variant?.offers])

  const connection = data?.itManagement?.distributorProductVariantsConnection
  const nodes = connection?.nodes

  const queryInFlight = variantId && !loadingProductCatalogContext && (loading || networkActivity)
  const queryFinishedEmpty =
    variantId &&
    !loadingProductCatalogContext &&
    data !== undefined &&
    !loading &&
    !networkActivity &&
    Array.isArray(nodes) &&
    nodes.length === 0

  const isLoading =
    (isDetailPath && !variantId) ||
    featureLoading ||
    (variantId && !variant && (loadingProductCatalogContext || queryInFlight))

  const hasDefinitiveNotFound = featureError || error || !variantId || queryFinishedEmpty

  if (isLoading) {
    return (
      <Page>
        <F0Box
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          padding="4xl"
          gap="md"
        >
          <Spinner size="large" />
          <F0Text
            variant="body"
            content={i18n.t('it_management.device_catalog.detail.loading_device')}
          />
        </F0Box>
      </Page>
    )
  }

  if (hasDefinitiveNotFound) {
    return (
      <Page>
        <F0Box padding="xl">
          <OneEmptyState
            title={i18n.t('it_management.device_catalog.detail.not_found')}
            description={i18n.t('it_management.device_catalog.detail.not_found_description')}
            variant="critical"
          />
        </F0Box>
      </Page>
    )
  }

  if (!variant) {
    return null
  }

  const deviceName = variant.product?.name || variant.sku || variant.id
  const productMediaImages: string[] = Array.isArray(variant.productMediaImageUrls)
    ? variant.productMediaImageUrls.map((url) => buildProductImageUrl(url))
    : []
  const deviceImages =
    productMediaImages.length > 0
      ? productMediaImages
      : [buildProductImageUrl(variant.firstProductMediaImageUrl, emptyStateImage)]

  const deviceAttributes = Array.isArray(variant.productAttributes)
    ? variant.productAttributes.map((attr) => ({
        id: attr.id,
        name: attr.name,
        value: attr.value,
      }))
    : []

  const deviceSpecifications = Array.isArray(variant.product?.productSpecifications)
    ? variant.product.productSpecifications.map((spec) => ({
        id: spec.id,
        name: spec.name,
        value: spec.value,
      }))
    : []

  const offers = variant.offers ?? []
  const selectedOffer =
    offers.find((o) => o.id === selectedOfferId) ??
    offers.find((o) => o.id === variant.firstOfferId) ??
    offers[0]
  const buyOffer = offers.find((o) => o.type === 'onetime')
  const isBuyMode =
    selectedOffer?.type === 'onetime' || (offers.length === 0 && variant.firstOfferCost != null)
  const basePrice = selectedOffer?.cost ?? variant.firstOfferCost

  const pricingLineItems: Array<{ label: string; amount: string }> = []
  const formattedBasePrice = formatCurrency(toCents(basePrice))
  if (isBuyMode) {
    pricingLineItems.push({
      label: i18n.t('it_management.device_catalog.detail.device_line_item'),
      amount: formattedBasePrice,
    })
    if (effectiveInsuranceEnabled) {
      pricingLineItems.push({
        label: i18n.t('it_management.device_catalog.detail.device_insurance_title'),
        amount: i18n.t('it_management.device_catalog.detail.device_insurance_price', {
          price: formatCurrency(toCents(INSURANCE_PRICE)),
        }),
      })
    }
    if (effectiveItSubscriptionEnabled) {
      pricingLineItems.push({
        label: i18n.t('it_management.device_catalog.detail.it_subscription_title'),
        amount: i18n.t('it_management.device_catalog.detail.it_subscription_price', {
          price: formatCurrency(toCents(IT_SUBSCRIPTION_PRICE)),
        }),
      })
    }
  } else if (buyOffer && buyOffer.cost != null) {
    pricingLineItems.push({
      label: i18n.t('it_management.device_catalog.detail.device_line_item'),
      amount: formatCurrency(toCents(buyOffer.cost)),
    })
  }

  const addOnTotal =
    (effectiveInsuranceEnabled ? INSURANCE_PRICE : 0) +
    (effectiveItSubscriptionEnabled ? IT_SUBSCRIPTION_PRICE : 0)
  const totalAmount = basePrice + addOnTotal

  const total = {
    label: i18n.t('it_management.device_catalog.detail.total_label'),
    amount: formatCurrency(toCents(totalAmount)),
  }

  const rentCreditInfo =
    !isBuyMode && selectedOffer
      ? {
          fromLabel: i18n.t('it_management.device_catalog.detail.from_label'),
          pricePerMonth: i18n.t('it_management.device_catalog.detail.from_price_per_month', {
            amount: formatCurrency(toCents(selectedOffer.cost ?? 0)),
          }),
          periodIncludes: i18n.t(
            'it_management.device_catalog.detail.rent_credit_period_includes',
            { months: selectedOffer.period ?? 24 }
          ),
        }
      : undefined

  const handleAddToCart = () => {
    const offerId = selectedOffer?.id ?? variant.firstOfferId
    const price = selectedOffer?.cost ?? variant.firstOfferCost
    const offerType =
      selectedOffer?.type === 'onetime' ||
      selectedOffer?.type === 'renting' ||
      selectedOffer?.type === 'credit'
        ? selectedOffer.type
        : 'onetime'
    const offerPeriod = selectedOffer?.period ?? null

    track(trackingEvents.itManagement.deviceCatalogEvents.addToCart, {
      device_id: variant.id,
      device_name: deviceName,
      price,
      origin: 'detail_page',
      company_id: company.id,
      user_id: access.id,
    })

    const cartItem: CartItem = {
      variantId: variant.id,
      productId: variant.product?.id ?? variant.id,
      name: deviceName,
      price,
      image: deviceImages[0] || '',
      quantity,
      addons: effectiveItSubscriptionEnabled
        ? [{ id: 'it-subscription', label: 'Factorial IT', pricePerMonth: IT_SUBSCRIPTION_PRICE }]
        : [],
      offerId: offerId ?? '',
      offerType,
      offerPeriod,
    }

    cart.addItem(cartItem)
  }

  const handleSave = () => {
    void toggleSaved(variant.id)
  }

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading || loadingProductCatalogContext}
    >
      <div
        className="grid gap-8 border-t border-f1-border pb-6 -mt-4"
        style={{ gridTemplateColumns: '1fr 380px' }}
      >
        <div className="min-w-0">
          <DeviceImageCarousel images={deviceImages} />

          {variant.product?.description && (
            <div className="py-5">
              <F0Text variant="description" content={variant.product.description} />
            </div>
          )}

          <DeviceSpecifications attributes={deviceAttributes} />
          {deviceSpecifications.length > 0 && (
            <DeviceSpecifications attributes={deviceSpecifications} />
          )}
        </div>

        <div className="md:sticky md:top-0 md:self-start">
          <DetailSidebar
            selectedCountry={selectedCountry}
            onCountryChange={setSelectedCountry}
            keyboardLanguage={keyboardLanguage}
            onKeyboardLanguageChange={setKeyboardLanguage}
            insuranceEnabled={effectiveInsuranceEnabled}
            onInsuranceChange={setInsuranceEnabled}
            itSubscriptionEnabled={effectiveItSubscriptionEnabled}
            onItSubscriptionChange={setItSubscriptionEnabled}
            offers={offers}
            selectedOfferId={selectedOfferId}
            onSelectedOfferChange={setSelectedOfferId}
            pricingLineItems={pricingLineItems}
            total={total}
            rentCreditInfo={rentCreditInfo}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onAddToCart={handleAddToCart}
            onSave={handleSave}
            isSaved={isSaved(variant.id)}
            isSavePending={isPending(variant.id)}
            cartItemCount={cart.itemCount}
            onOpenCart={() => {
              track(trackingEvents.itManagement.deviceCatalogEvents.openCart, {
                item_count: cart.itemCount,
                company_id: company.id,
                user_id: access.id,
              })
              cart.openCart()
            }}
          />
        </div>
      </div>

      <F0Box width={'full'} display="flex" flexDirection="column" alignItems="center" gap="md">
        <div className="w-full max-w-2xl px-6 py-8">
          <MarketplaceFAQ
            title={i18n.t('it_management.device_catalog.detail.faq_title')}
            items={buildFaqItems()}
          />
        </div>

        {showSantanderLegalBlock ? (
          <div className="w-full max-w-2xl px-6 py-8">
            <div className="flex flex-col gap-2">
              <F0Text
                variant="label"
                content={i18n.t('it_management.device_catalog.detail.legal_title')}
              />
              <F0Text
                variant="description"
                content={i18n.t('it_management.device_catalog.detail.legal_text')}
              />
            </div>
          </div>
        ) : null}
      </F0Box>
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogDetailPage
